import {
  Component,
  AfterViewInit,
  Input,
  OnChanges,
  inject,
  ViewChild,
  ElementRef,
  input,
  effect,
  ChangeDetectionStrategy
} from '@angular/core';
import * as d3 from 'd3';
import { ThemeService } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

interface PieItem {
  label: string;
  value: number;
  color?: string;
  /** Override the value shown in the tooltip (arc proportion still uses `value`). */
  displayValue?: number;
  /** When true, the percentage is not shown in the tooltip. */
  hidePercentage?: boolean;
}

@Component({
  selector: 'app-pie-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './pie-chart.component.html',
})
export class PieChartComponent implements AfterViewInit, OnChanges {
  @Input() showLabelsOnHover: boolean = false;
  @Input() showLegend: boolean = true;
  @Input() showLegendValues: boolean = true;
  @Input() keepLabelsOnHover: boolean = false;
  @Input() data: PieItem[] = [];
  @Input() width: number | 'auto' = 0;
  @Input() height: number | 'auto' = 0;
  @Input() currentTheme: 'light' | 'dark' = 'dark';
  @Input() addGradient: boolean = false;
  @Input() type: 'default' | 'slices' = 'default';
  @Input() mode: 'default' | 'semi' = 'default';
  @Input() centerValue: number | string | null = null;
  @Input() centerLabel: string = '';
  @Input() numSlices: number = 60;
  @Input() legendTextColored: boolean = true;
  @Input() legendPosition: 'bottom' | 'right' | 'top' | 'left' = 'bottom';
  @Input() is3d: boolean = false;
  @Input() hideZeroValues: boolean = false;
  @Input() showPolylines: boolean = false;
  @Input() polylineValueFormat: 'percentage' | 'value' = 'percentage';
  @Input() isDonut: boolean = false;
  @Input() donutRadiusRatio: number = 0.45;
  @Input() pieScale: number = 0.65;
  @Input() useParentHeight: boolean = true;
  @Input() minHeight: number = 200;
  /** Scale SVG to stay within the chart container bounds. */
  fitToContainer = input(false);
  /** Use width/height inputs for layout instead of measuring the container. */
  preferInputDimensions = input(false);
  /** Scales polyline label reach when fitToContainer is enabled (0–1). */
  labelReachScale = input(1);
  /** Fit viewBox tightly to rendered content so the chart fills its container. */
  tightViewBox = input(false);
  @ViewChild('pieChartRef') pieChartRef: ElementRef | undefined;
  @ViewChild('legendRefBottom') legendRefBottom: ElementRef | undefined;
  @ViewChild('legendRefRight') legendRefRight: ElementRef | undefined;
  @ViewChild('legendRefTop') legendRefTop: ElementRef | undefined;
  @ViewChild('legendRefLeft') legendRefLeft: ElementRef | undefined;

  private themeService = inject(ThemeService);
  private themeSubscription?: Subscription;
  private resizeObserver?: ResizeObserver;
  private isViewInitialized = false;

  constructor() {
    effect(() => {
      this.fitToContainer();
      this.preferInputDimensions();
      this.labelReachScale();
      this.tightViewBox();
      if (this.isViewInitialized && this.data?.length) {
        this.createChart();
      }
    });
  }

  ngAfterViewInit() {
    this.isViewInitialized = true;
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      setTimeout(() => {
        this.createChart();
      }, 0);
    });

    // Observe container resize for responsive re-rendering
    if (this.pieChartRef?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        this.createChart();
      });
      this.resizeObserver.observe(this.pieChartRef.nativeElement);
    }

    // Initial draw
    this.createChart();
  }

  ngOnChanges() {
    if (this.data?.length && this.isViewInitialized) {
      this.createChart();
    }
  }



  private getColors() {
    const isDark = this.currentTheme === 'dark';

    return {
      background: isDark ? '#232327' : '#F5F5F5',
      text: isDark ? '#ffffff' : '#141437',
      shadow: isDark
        ? '0px 4px 20px rgba(0,0,0,0.35)'
        : '0px 4px 20px rgba(0,0,0,0.15)',
      border: isDark ? '1px solid #2A2A2E' : '1px solid #EAEAEA',
      subtitleColor: isDark ? '#A1A1AA' : '#7D7D93',
      sliceColors: [
        '#F59E0B', // Morning (Orange)
        '#EC4899', // Afternoon (Pink/Red)
        '#7C3AED', // Evening (Purple)
        '#3B82F6',
        '#10B981',
      ],
      axisLine: isDark ? '#ffffff33' : '#00000022',
    };
  }

  private createChart() {
    if (!this.data?.length) return;
    if (!this.pieChartRef) return;

    const containerElement = this.pieChartRef.nativeElement;
    const container = d3.select(containerElement);
    container.selectAll('*').remove();

    // Clear legends
    const legendRefs = [this.legendRefBottom, this.legendRefRight, this.legendRefTop, this.legendRefLeft];
    legendRefs.forEach(ref => {
      if (ref) d3.select(ref.nativeElement).selectAll('*').remove();
    });

    // Select active legend container based on position
    let legendContainer;
    if (this.showLegend) {
      if (this.legendPosition === 'right' && this.legendRefRight) {
        legendContainer = d3.select(this.legendRefRight.nativeElement);
      } else if (this.legendPosition === 'left' && this.legendRefLeft) {
        legendContainer = d3.select(this.legendRefLeft.nativeElement);
      } else if (this.legendPosition === 'top' && this.legendRefTop) {
        legendContainer = d3.select(this.legendRefTop.nativeElement);
      } else if (this.legendPosition === 'bottom' && this.legendRefBottom) {
        legendContainer = d3.select(this.legendRefBottom.nativeElement);
      }
    }

    if (!containerElement) return;

    const isSemi = this.mode === 'semi';
    const rect = containerElement.getBoundingClientRect();
    const useInputDimensions = this.preferInputDimensions();
    let width = useInputDimensions && typeof this.width === 'number'
      ? this.width
      : (rect.width || containerElement.clientWidth || (typeof this.width === 'number' ? this.width : 320));
    let height = this.useParentHeight
      ? (rect.height || containerElement.clientHeight)
      : (useInputDimensions && typeof this.height === 'number'
        ? this.height
        : (typeof this.height === 'number' ? this.height : 320));
    if (height === 0) height = width;

    if (this.fitToContainer() && this.tightViewBox()) {
      const containerW = rect.width || containerElement.clientWidth;
      const containerH = rect.height || containerElement.clientHeight;
      if (containerW > 0) width = containerW;
      if (containerH > 0) height = containerH;
    }

    // fitToContainer only controls SVG scaling — layout may use container when tightViewBox is on.

    const padding = 10;
    const containerPaddingForRadius = 10; // Tightening internal padding to maximize chart size
    const radius = isSemi
      ? Math.min(width / 2, height) - padding / 2
      : (Math.min(width, height) - containerPaddingForRadius) / 2;

    const innerRadius = this.type === 'slices' ? radius * 0.6 : 0;

    const horizontalShift = (this.showLegend && this.legendPosition === 'right') ? 0.4
      : (this.showLegend && this.legendPosition === 'left') ? 0.6
        : 0.5;
    const verticalShift = (this.showLegend && this.legendPosition === 'bottom') ? 0.42
      : (this.showLegend && this.legendPosition === 'top') ? 0.55
        : 0.5;

    const MIN_PERCENT = 2;
    const total = d3.sum(this.data, (d) => d.value);

    let pieData: any[] = [];
    let colorScale: any;
    const colors = this.getColors();
    // Per-category color lookup (slices mode): bypasses degenerate piecewise-scale issues.
    // Falls back gracefully for default/non-slices rendering paths.
    const categoryColorMap: string[] = this.data.map((d, i) =>
      d['color'] || colors.sliceColors[i % colors.sliceColors.length]
    );


    if (this.type === 'slices') {
      const numSlots = this.numSlices;

      // No data — distribute slots equally if total is 0
      const slotsPerValue = this.data.map(d => total > 0 ? Math.round((d.value / total) * numSlots) : Math.floor(numSlots / this.data.length));
      // Fix rounding so total is exactly numSlots
      const slotSum = d3.sum(slotsPerValue);
      const slotDiff = numSlots - slotSum;
      if (slotDiff !== 0) {
        const maxIdx = slotsPerValue.indexOf(Math.max(...slotsPerValue));
        slotsPerValue[maxIdx] += slotDiff;
      }

      // Color scale: only categories that actually have slots
      const activeCategories = this.data.filter((_, i) => slotsPerValue[i] > 0);

      if (activeCategories.length === 1) {
        const solidColor = activeCategories[0]['color'] || colors.sliceColors[0];
        colorScale = () => solidColor;
      } else if (activeCategories.length > 1) {
        const domain: number[] = [];
        const range: string[] = [];
        let cumulative = 0;

        activeCategories.forEach((d) => {
          const idx = this.data.indexOf(d);
          const catColor = d['color'] || colors.sliceColors[idx % colors.sliceColors.length];
          const weight = slotsPerValue[idx];

          if (this.addGradient) {
            const mid = cumulative + weight / 2;
            domain.push(mid);
            range.push(catColor);
          } else {
            domain.push(cumulative);
            range.push(catColor);
            cumulative += weight;
            domain.push(cumulative);
            range.push(catColor);
            return;
          }
          cumulative += weight;
        });

        if (this.addGradient && domain.length > 0) {
          if (domain[0] > 0) { domain.unshift(0); range.unshift(range[0]); }
          if (domain[domain.length - 1] < numSlots) { domain.push(numSlots); range.push(range[range.length - 1]); }
        }

        colorScale = d3.scaleLinear<string>()
          .domain(domain)
          .range(range)
          .interpolate(d3.interpolateRgb);
      } else {
        colorScale = () => colors.sliceColors[0];
      }

      let currentSlotIndex = 0;
      this.data.forEach((d, i) => {
        if (this.hideZeroValues && d.value === 0) return; // Controllable zero value skip
        const count = slotsPerValue[i];
        for (let j = 0; j < count; j++) {
          pieData.push({
            data: { ...d, label: d.label, value: 1, index: i, userColor: d['color'] },
            index: currentSlotIndex++,
            value: 1,
            userLabel: d.label,
            userValue: d.value,
            categoryIndex: i
          });
        }
      });
    } else {
      pieData = this.data
        .filter(d => !this.hideZeroValues || d.value > 0)
        .map((d, i) => {
          const pct = total > 0 ? (d.value / total) * 100 : 0;
          return {
            ...d,
            _percent: Math.max(pct, MIN_PERCENT),
            data: d,
            index: i,
            categoryIndex: this.data.indexOf(d)
          };
        });

      const dataColors = this.data.map((d, i) => d['color'] || colors.sliceColors[i % colors.sliceColors.length]);
      colorScale = d3.scaleOrdinal().domain(this.data.map((_, i) => i.toString())).range(dataColors);
    }

    // Count visible slices (non-zero) to decide if labels/polylines are meaningful
    const visibleSliceCount = this.data.filter(d => !this.hideZeroValues || d.value > 0).length;
    const canShowPolylines = this.showPolylines && !isSemi && this.type !== 'slices';
    const canShowOutsideLabels = (!this.showLabelsOnHover && this.type !== 'slices') || canShowPolylines;

    // Calculate dynamic padding for labels that extend beyond the core container
    let leftLabelPad = 0;
    let rightLabelPad = 0;
    let topLabelPad = 0;
    let bottomLabelPad = 0;

    if (canShowOutsideLabels && visibleSliceCount > 0) {
      const actualCenterX = (width + padding) * horizontalShift;
      const actualCenterY = (height + padding) * verticalShift;
      const reachScale = this.labelReachScale();
      const reachX = radius * 1.35 * reachScale;
      const reachY = radius * 1.15 * reachScale;
      const edgePad = 8;
      const labelTextPad = this.getPolylineLabelTextPad();
      const labelTextPadV = 28;

      if (canShowPolylines) {
        // Pie center is shifted right by leftLabelPad; account for text extending past endpoints.
        leftLabelPad = Math.max(0, reachX + labelTextPad + edgePad - actualCenterX);
        rightLabelPad = Math.max(0, actualCenterX + reachX + labelTextPad + edgePad - (width + padding));

        if (this.showLegend && this.legendPosition === 'right') {
          const reach = radius * 1.25 * reachScale;
          const currentSpace = width * horizontalShift;
          leftLabelPad = Math.max(leftLabelPad, Math.max(0, reach + labelTextPad - currentSpace) + edgePad);
          rightLabelPad = 0;
        } else if (this.showLegend && this.legendPosition === 'left') {
          const reach = radius * 1.25 * reachScale;
          const currentSpace = width * (1 - horizontalShift);
          rightLabelPad = Math.max(rightLabelPad, Math.max(0, reach + labelTextPad - currentSpace) + edgePad);
          leftLabelPad = 0;
        }

        topLabelPad = Math.max(0, reachY + labelTextPadV + edgePad - actualCenterY);
        bottomLabelPad = Math.max(0, actualCenterY + reachY + labelTextPadV + edgePad - (height + padding));
      } else {
        // Standard percentage labels (pushed by 25 units from radius*1.05)
        const vReach = radius * 1.05 + 20; // 25 push + ~5 for top of text
        const hReach = radius * 1.05 + 40; // 25 push + ~25 for text width

        topLabelPad = Math.max(0, vReach - actualCenterY);
        bottomLabelPad = Math.max(0, actualCenterY + vReach - (height + padding));
        leftLabelPad = Math.max(0, hReach - actualCenterX);
        rightLabelPad = Math.max(0, actualCenterX + hReach - (width + padding));
      }
    }

    // For semi-circle: SVG height is just the top half + padding
    const svgHeight = isSemi ? radius + padding + 10 : height + padding + topLabelPad + bottomLabelPad;
    const svgWidth = width + padding + leftLabelPad + rightLabelPad;

    const svgEl = container
      .append('svg')
      .attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    if (this.fitToContainer()) {
      svgEl
        .attr('width', '100%')
        .attr('height', '100%')
        .style('width', '100%')
        .style('height', '100%')
        .style('max-width', '100%')
        .style('max-height', '100%')
        .style('display', 'block')
        .style('overflow', 'hidden');
    } else {
      svgEl
        .attr('width', svgWidth)
        .attr('height', svgHeight)
        .style('width', 'auto')
        .style('height', 'auto')
        .style('max-width', 'none')
        .style('overflow', 'visible');
    }

    // Pie center: intelligently shifted based on legend position, plus pads for labels
    // horizontalShift and verticalShift are already defined above

    const pieCenterX = (isSemi
      ? (width + padding) / 2
      : (width + padding) * horizontalShift) + leftLabelPad;
    const pieCenterY = (isSemi ? radius + padding / 2 : (height + padding) * verticalShift) + topLabelPad;


    const svg = svgEl
      .append('g')
      .attr('transform', `translate(${pieCenterX}, ${pieCenterY})`);

    // 3D perspective: squash Y axis so the circle looks elliptical (tilted)
    if (this.is3d && !isSemi) {
      svg.attr('transform',
        `translate(${pieCenterX}, ${pieCenterY}) scale(1, ${this.pieScale})`);
    }

    // Start first slice at exact top (12 o'clock) for 'on top' positioning
    const polylinesStartOffset = 0;

    const pie = d3
      .pie<any>()
      .value((d) => this.type === 'slices' ? 1 : d._percent)
      .sort(null)
      .padAngle(this.type === 'slices' ? 0.02 : 0)
      .startAngle(isSemi ? -Math.PI / 2 : polylinesStartOffset)
      .endAngle(isSemi ? Math.PI / 2 : 2 * Math.PI + polylinesStartOffset);

    const arc = d3
      .arc<any>()
      .innerRadius(this.type === 'slices' ? radius * 0.5 : (this.isDonut ? radius * this.donutRadiusRatio : 0))
      .outerRadius(radius - 10)
      .cornerRadius(this.type === 'slices' ? 2 : 0);

    const radiusLabel = radius * 1.05;
    const labelArc = d3
      .arc<any>()
      .innerRadius(radiusLabel)
      .outerRadius(radiusLabel);

    const tooltip = container
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('z-index', '20')
      .style('background', colors.background)
      .style('border', colors.border)
      .style('border-radius', '20px')
      .style('padding', '16px 20px')
      .style('box-shadow', colors.shadow)
      .style('color', colors.text)
      .style('font-family', 'Inter, sans-serif')
      .style('white-space', 'nowrap');

    // Positions the tooltip near the pointer while ensuring it never escapes
    // the chart container on any of the four edges. The container can be very
    // narrow on mobile (e.g. a 300×300 donut), so without left/top clamping a
    // wide tooltip easily gets cut off.
    const positionTooltip = (event: { offsetX: number; offsetY: number }) => {
      const tooltipNode = tooltip.node() as HTMLElement | null;
      if (!tooltipNode) return;

      const rect = tooltipNode.getBoundingClientRect();
      const tooltipWidth = rect.width;
      const tooltipHeight = rect.height;

      const containerWidth = containerElement.clientWidth;
      const containerHeight = containerElement.clientHeight;
      const edgePadding = 8;
      const pointerGap = 20;

      // Horizontal placement: try right of pointer, then left, then clamp.
      let left = event.offsetX + pointerGap;
      if (left + tooltipWidth > containerWidth - edgePadding) {
        left = event.offsetX - tooltipWidth - pointerGap;
      }
      if (left < edgePadding) {
        left = edgePadding;
      }
      if (left + tooltipWidth > containerWidth - edgePadding) {
        left = Math.max(edgePadding, containerWidth - tooltipWidth - edgePadding);
      }

      // Vertical placement: try above pointer, then below, then clamp.
      let top = event.offsetY - tooltipHeight - pointerGap;
      if (top < edgePadding) {
        top = event.offsetY + pointerGap;
      }
      if (top + tooltipHeight > containerHeight - edgePadding) {
        top = Math.max(edgePadding, containerHeight - tooltipHeight - edgePadding);
      }

      tooltip
        .style('left', `${left}px`)
        .style('top', `${top}px`);
    };

    const depth = 22; // extrusion depth
    const pieArcs = pie(pieData);

    // Sort arcs back-to-front for correct 3D overlap in tilted perspective
    // Slices "behind" the center have more negative centroid.y in the tilted view
    const sortedArcs = [...pieArcs].sort((a, b) => {
      const aMid = (a.startAngle + a.endAngle) / 2;
      const bMid = (b.startAngle + b.endAngle) / 2;
      // In 3D: y = -cos(angle). Back slices are those with angle near 0/2pi (top)
      // Front slices are near pi (bottom). We want to draw back first.
      return Math.cos(bMid) - Math.cos(aMid);
    });

    const sliceGroups = svg
      .selectAll('g.slice-group')
      .data(sortedArcs)
      .enter()
      .append('g')
      .attr('class', (d: any) => {
        const catIdx = d.data.categoryIndex;
        return `slice-group slice-cat-${catIdx}`;
      })
      .style('transition', '0.2s transform ease-out');

    // Draw the 3D components for each group if is3d
    if (this.is3d && !isSemi) {
      sliceGroups.each((d: any, i, nodes) => {
        const g = d3.select(nodes[i]);
        const catIdx = d.data.categoryIndex;
        const color = this.type === 'slices'
          ? (categoryColorMap[catIdx] || colorScale(d.data.index))
          : colorScale(catIdx.toString());
        const sideColor = d3.color(color)?.darker(0.05)?.formatHex() || color;
        const r = radius - 10;
        const rInner = this.type === 'slices' ? radius * 0.5 : (this.isDonut ? radius * this.donutRadiusRatio : 0);

        // 1. Draw "crust" wall (outer edge)
        const steps = Math.max(4, Math.round(Math.abs(d.endAngle - d.startAngle) / (Math.PI / 16)));
        const angleStep = (d.endAngle - d.startAngle) / steps;
        const outerWallPoints: [number, number][] = [];
        for (let s = 0; s <= steps; s++) {
          const a = d.startAngle + s * angleStep - Math.PI / 2;
          outerWallPoints.push([r * Math.cos(a), r * Math.sin(a)]);
        }
        let wallPath = `M ${outerWallPoints[0][0]},${outerWallPoints[0][1]}`;
        for (const [px, py] of outerWallPoints) wallPath += ` L ${px},${py}`;
        for (let s = steps; s >= 0; s--) wallPath += ` L ${outerWallPoints[s][0]},${outerWallPoints[s][1] + depth}`;
        wallPath += ' Z';

        g.append('path').attr('class', 'slice-wall-outer').attr('d', wallPath).attr('fill', sideColor).attr('opacity', 0.9);

        // 2. Draw radial walls (interior faces)
        const drawRadialWall = (angle: number) => {
          const a = angle - Math.PI / 2;
          const xO = r * Math.cos(a), yO = r * Math.sin(a);
          const xI = rInner * Math.cos(a), yI = rInner * Math.sin(a);
          const path = `M ${xO},${yO} L ${xI},${yI} L ${xI},${yI + depth} L ${xO},${yO + depth} Z`;
          g.append('path').attr('class', 'slice-wall-radial').attr('d', path).attr('fill', d3.color(sideColor)?.darker(0.01)?.formatHex() || sideColor);
        };
        drawRadialWall(d.startAngle);
        drawRadialWall(d.endAngle);

        // 3. Draw inner wall if donut
        if (rInner > 0) {
          let innerWallPath = `M ${rInner * Math.cos(d.startAngle - Math.PI / 2)},${rInner * Math.sin(d.startAngle - Math.PI / 2)}`;
          for (let s = 0; s <= steps; s++) {
            const a = d.startAngle + s * angleStep - Math.PI / 2;
            innerWallPath += ` L ${rInner * Math.cos(a)},${rInner * Math.sin(a)}`;
          }
          for (let s = steps; s >= 0; s--) {
            const a = d.startAngle + s * angleStep - Math.PI / 2;
            innerWallPath += ` L ${rInner * Math.cos(a)},${rInner * Math.sin(a) + depth}`;
          }
          innerWallPath += ' Z';
          g.append('path').attr('class', 'slice-wall-inner').attr('d', innerWallPath).attr('fill', sideColor).attr('opacity', 0.8);
        }
      });
    }

    const hoverArc = d3
      .arc<any>()
      .innerRadius(this.type === 'slices' ? radius * 0.6 : (this.isDonut ? radius * this.donutRadiusRatio : 0))
      .outerRadius(radius - 5);

    sliceGroups
      .append('path')
      .attr('class', 'slice-top')
      .attr('d', arc)
      .attr('fill', (d: any) => {
        const idx = d.data.categoryIndex;
        const baseColor = this.type === 'slices'
          ? (categoryColorMap[idx] || colorScale(d.data.index))
          : colorScale(idx.toString());
        if (this.addGradient && baseColor) {
          const cleanColor = baseColor.replace('#', '').replace(/[^a-zA-Z0-9]/g, '');
          return `url(#pie-grad-${cleanColor})`;
        }
        return baseColor;
      })
      .style('transition', '0.20s ease-out');

    // Decorative paths should not steal hover from the dedicated hit layer.
    sliceGroups
      .selectAll('.slice-wall-outer, .slice-wall-radial, .slice-wall-inner, .slice-top')
      .style('pointer-events', 'none');

    sliceGroups
      .append('path')
      .attr('class', 'slice-hit')
      .attr('d', hoverArc)
      .attr('fill', 'transparent')
      .attr('stroke', 'none')
      .style('pointer-events', 'all')
      .style('cursor', 'pointer');

    if (this.addGradient) {
      const defs = svgEl.append('defs');
      const uniqueColors = new Set<string>();

      // Collect colors to create gradients for.
      // In slices mode use categoryColorMap directly so gradient IDs match
      // what the fill path references (url(#pie-grad-XX)). Using colorScale(i)
      // would yield interpolated colors whose IDs never match, causing blank arcs.
      if (this.type === 'slices') {
        categoryColorMap.forEach(c => { if (c) uniqueColors.add(c); });
      } else {
        this.data.forEach((_, i) => {
          const c = colorScale(i.toString());
          if (c) uniqueColors.add(c);
        });
      }

      uniqueColors.forEach(color => {
        const cleanColor = color.replace('#', '').replace(/[^a-zA-Z0-9]/g, '');
        const grad = defs.append('linearGradient')
          .attr('id', `pie-grad-${cleanColor}`)
          .attr('x1', '0%').attr('y1', '0%')
          .attr('x2', '0%').attr('y2', '100%');

        const endOpacity = this.is3d ? 1 : 0.6;
        const endColor = this.is3d ? (d3.color(color)?.darker(0.1)?.formatHex() || color) : color;

        grad.append('stop').attr('offset', '0%').attr('stop-color', color).attr('stop-opacity', 1);
        grad.append('stop').attr('offset', '100%').attr('stop-color', endColor).attr('stop-opacity', endOpacity);
      });
    }

    let currentHoveredCategory: number | null = null;
    let hideTooltipTimer: ReturnType<typeof setTimeout> | null = null;
    const overlayRoot = this.is3d && !isSemi ? svgEl : svg;

    const clearHideTooltipTimer = () => {
      if (hideTooltipTimer !== null) {
        clearTimeout(hideTooltipTimer);
        hideTooltipTimer = null;
      }
    };

    const showTooltip = (event: MouseEvent, d: any) => {
      const categoryIdx = d.data.categoryIndex;
      const color = this.type === 'slices'
        ? (categoryColorMap[categoryIdx] || colorScale(d.data.index))
        : colorScale(categoryIdx.toString());

      const midAngle = (d.startAngle + d.endAngle) / 2;
      const explodeDist = 12;
      const tx = Math.sin(midAngle) * explodeDist;
      const ty = -Math.cos(midAngle) * explodeDist;

      const sliceGroup = d3.select((event.currentTarget as Element).parentNode as Element);
      sliceGroup
        .transition().duration(200)
        .attr('transform', `translate(${tx}, ${ty})`)
        .style('filter', 'drop-shadow(0px 3px 6px rgba(0,0,0,0.3))');

      if (!this.keepLabelsOnHover) {
        overlayRoot.select(`.label-cat-${categoryIdx}`)
          .transition().duration(150)
          .style('opacity', 0);

        overlayRoot.select(`.polyline-cat-${categoryIdx}`)
          .transition().duration(150)
          .style('opacity', 0);
      }

      clearHideTooltipTimer();
      currentHoveredCategory = categoryIdx;

      const label = this.type === 'slices'
        ? d.data.userLabel
        : (d.data.label ?? d.data.data?.label ?? '');
      const value = this.type === 'slices' ? d.data.userValue : d.data.value;
      const shownValue = d.data.data?.displayValue ?? value;

      tooltip.style('opacity', 1).html(`
          <div style="font-size:14px;font-weight:700;margin-bottom:8px;border-bottom:1px solid ${colors.border.replace('1px solid ', '')};padding-bottom:8px;">${label}</div>
          <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;">
            <div style="display:flex;align-items:center;gap:8px;">
              <div style="width:10px;height:10px;border-radius:50%;background-color:${color};"></div>
              <span style="font-size:12px;font-weight:500;opacity:0.7;">Value:</span>
            </div>
            <span style="font-size:12px;font-weight:700;">${shownValue.toLocaleString()}</span>
          </div>
      `);

      positionTooltip(event);
    };

    const hideTooltip = (event: MouseEvent, d: any) => {
      const categoryIdx = d.data.categoryIndex;

      const sliceGroup = d3.select((event.currentTarget as Element).parentNode as Element);
      sliceGroup
        .transition().duration(200)
        .attr('transform', 'translate(0,0)')
        .style('filter', 'none');

      overlayRoot.select(`.label-cat-${categoryIdx}`)
        .transition().duration(150)
        .style('opacity', 1);

      overlayRoot.select(`.polyline-cat-${categoryIdx}`)
        .transition().duration(150)
        .style('opacity', 0.8);

      clearHideTooltipTimer();
      hideTooltipTimer = setTimeout(() => {
        if (currentHoveredCategory === categoryIdx) {
          currentHoveredCategory = null;
          tooltip.style('opacity', 0);
        }
        hideTooltipTimer = null;
      }, 50);
    };

    sliceGroups
      .selectAll<SVGPathElement, unknown>('.slice-hit')
      .on('mouseover', showTooltip)
      .on('mousemove', (event) => {
        positionTooltip(event);
      })
      .on('mouseout', hideTooltip);

    if (canShowPolylines) {
      const pieArcs = pie(pieData);
      const outerArc = d3.arc<any>().innerRadius(radius * 1.05).outerRadius(radius * 1.05);
      const minLabelPct = 0; // Show all labels regardless of how small the slice is

      // Render polylines/labels outside the 3D-scaled group so they aren't squished
      const polylineContainer = this.is3d && !isSemi
        ? svgEl.append('g').attr('transform', `translate(${pieCenterX}, ${pieCenterY})`)
        : svg;
      const polylineGroup = polylineContainer.append('g')
        .attr('class', 'pie-polylines')
        .style('pointer-events', 'none');
      const labelGroup = polylineContainer.append('g')
        .attr('class', 'pie-labels-outside')
        .style('pointer-events', 'none');

      let labels = pieArcs.map((d: any) => {
        const midAngle = (d.startAngle + d.endAngle) / 2;
        let position: 'left' | 'right' | 'top' | 'bottom' = midAngle < Math.PI ? 'right' : 'left';

        // Force redirection if legend is on that side - using vertical poles
        if (this.showLegend) {
          if (this.legendPosition === 'right' && position === 'right') {
            position = midAngle < Math.PI / 2 ? 'top' : 'bottom';
          } else if (this.legendPosition === 'left' && position === 'left') {
            position = midAngle < 1.5 * Math.PI ? 'bottom' : 'top';
          }
        }
        const catIdx = d.data.categoryIndex;
        const itemColor = categoryColorMap[catIdx] || colorScale(catIdx.toString());
        // Use the original value for percentage, not the min-clamped _percent
        const originalValue = d.data.data?.value ?? d.data.value;

        // Apply 3D squash factor manually to positions since we render them outside the 3D scale group
        const squash = (this.is3d && !isSemi) ? this.pieScale : 1;
        const oCentroid = outerArc.centroid(d);
        const eCentroid = d3.arc<any>().innerRadius(radius - 10).outerRadius(radius - 10).centroid(d);

        return {
          d,
          midAngle,
          position,
          color: itemColor,
          pct: (originalValue / total) * 100,
          midPos: [oCentroid[0], oCentroid[1] * squash],
          edgePos: [eCentroid[0], eCentroid[1] * squash]
        };
      }).filter((l: any) => l.pct >= minLabelPct);

      const minGap = 55; // Increased gap to prevent labels like TikTok/Google from overlapping
      const resolveCollisions = (sideLabels: any[]) => {
        sideLabels.sort((a, b) => a.midPos[1] - b.midPos[1]);
        let maxIterations = 20;
        while (maxIterations > 0) {
          let hasCollision = false;
          for (let i = 1; i < sideLabels.length; i++) {
            const prev = sideLabels[i - 1];
            const curr = sideLabels[i];
            const diff = curr.midPos[1] - prev.midPos[1];
            if (diff < minGap) {
              const overlap = minGap - diff;
              prev.midPos[1] -= overlap / 2;
              curr.midPos[1] += overlap / 2;
              hasCollision = true;
            }
          }
          if (!hasCollision) break;
          maxIterations--;
        }
      };

      const rightLabels = labels.filter((l: any) => l.position === 'right');
      const leftLabels = labels.filter((l: any) => l.position === 'left');
      resolveCollisions(rightLabels);
      resolveCollisions(leftLabels);

      // Horizontal collision resolver for poles
      const resolveHorizontalCollisions = (poleLabels: any[]) => {
        poleLabels.sort((a, b) => a.midPos[0] - b.midPos[0]);
        const hGap = 65;
        let maxIterations = 20;
        while (maxIterations > 0) {
          let hasCollision = false;
          for (let i = 1; i < poleLabels.length; i++) {
            const prev = poleLabels[i - 1];
            const curr = poleLabels[i];
            const diff = curr.midPos[0] - prev.midPos[0];
            if (diff < hGap) {
              const overlap = hGap - diff;
              prev.midPos[0] -= overlap / 2;
              curr.midPos[0] += overlap / 2;
              hasCollision = true;
            }
          }
          if (!hasCollision) break;
          maxIterations--;
        }
      };

      const topLabels = labels.filter((l: any) => l.position === 'top');
      const bottomLabels = labels.filter((l: any) => l.position === 'bottom');
      resolveHorizontalCollisions(topLabels);
      resolveHorizontalCollisions(bottomLabels);

      labels.forEach((l: any) => {
        // For single-slice case: keep polyline but anchor it to the top of the pie
        if (visibleSliceCount === 1) {
          l.position = 'right';
          l.edgePos = [0, -(radius - 10)]; // Top edge of pie
          l.midPos = [radius * 0.15, -(radius * 1.05)]; // Slightly right, above
        }

        let endPos = [0, 0];
        let textAnchor = 'middle';
        const reachScale = this.labelReachScale();
        const reachX = radius * 1.35 * reachScale;
        const reachY = radius * 1.15 * reachScale;

        if (l.position === 'right') {
          endPos = [reachX, l.midPos[1]];
          textAnchor = 'start';
        } else if (l.position === 'left') {
          endPos = [-reachX, l.midPos[1]];
          textAnchor = 'end';
        } else if (l.position === 'top') {
          endPos = [l.midPos[0], -reachY];
          textAnchor = 'middle';
        } else if (l.position === 'bottom') {
          endPos = [l.midPos[0], reachY];
          textAnchor = 'middle';
        }

        // Shorten the polyline endpoint to avoid overlapping the label text
        let polyEnd = [...endPos];
        const gap = 20;
        if (l.position === 'top') polyEnd[1] += gap;
        else if (l.position === 'bottom') polyEnd[1] -= gap;
        else if (l.position === 'left') polyEnd[0] += gap;
        else if (l.position === 'right') polyEnd[0] -= gap;

        // Refined polyline points - clean L-bends for top/bottom poles
        let points = '';
        if (l.position === 'top' || l.position === 'bottom') {
          // Go straight up/down to the polyline's final Y level, then over to its horizontal end
          points = `${l.edgePos[0]},${l.edgePos[1]} ${l.edgePos[0]},${polyEnd[1]} ${polyEnd[0]},${polyEnd[1]}`;
        } else {
          // Standard elbow for side labels
          points = `${l.edgePos[0]},${l.edgePos[1]} ${l.midPos[0]},${l.midPos[1]} ${polyEnd[0]},${polyEnd[1]}`;
        }

        polylineGroup.append('polyline')
          .attr('class', `polyline-cat-${l.d.data.categoryIndex}`)
          .attr('points', points)
          .style('fill', 'none')
          .style('stroke', l.color)
          .style('stroke-width', '1px')
          .style('opacity', 0.8);

        const textGroup = labelGroup.append('text')
          .attr('class', `pie-label label-cat-${l.d.data.categoryIndex}`)
          .attr('transform', `translate(${endPos[0]}, ${endPos[1]})`)
          .attr('text-anchor', textAnchor)
          .style('font-family', 'Inter, sans-serif');

        const isSmall = width < 300;
        const labelFontSize = isSmall ? '13px' : '15px';
        const labelFontWeight = isSmall ? '500' : '600';
        const valueFontSize = isSmall ? '15px' : '17px';
        const valueFontWeight = isSmall ? '600' : '700';

        textGroup.append('tspan')
          .attr('x', 0)
          .attr('dy', '-0.3em')
          .style('font-size', labelFontSize)
          .style('font-weight', labelFontWeight)
          .style('fill', this.currentTheme === 'dark' ? '#D1D5DB' : '#4B5563')
          .text(l.d.data.userLabel || l.d.data.label);

        textGroup.append('tspan')
          .attr('x', 0)
          .attr('dy', '1.4em')
          .style('font-size', valueFontSize)
          .style('font-weight', valueFontWeight)
          .style('fill', l.color)
          .text(this.polylineValueFormat === 'value' ? (l.d.data.data?.value ?? l.d.data.value) : `${parseFloat(l.pct.toFixed(2))}%`);
      });
    } else if (!this.showLabelsOnHover && this.type !== 'slices') {
      const pieArcs = pie(pieData);
      const minLabelPct = 0.01;
      const labelFontSize = 12;
      const minGap = labelFontSize + 8; // Increased gap for better readability

      interface LabelDatum {
        d: any;
        x: number;
        y: number;
        anchor: string;
        text: string;
        pct: number;
      }

      const allLabels: LabelDatum[] = pieArcs.map((d: any) => {
        const midAngle = (d.startAngle + d.endAngle) / 2;
        let [x, y] = labelArc.centroid(d);
        const push = 25;
        x += Math.sin(midAngle) * push;
        y -= Math.cos(midAngle) * push;

        // Move bottom-most labels up slightly
        if (Math.cos(midAngle) < -0.9) {
          y -= 12;
        }

        const anchor = midAngle < Math.PI ? 'start' : 'end';
        const value = d.data.value;
        const pct = (value / total) * 100;
        const text = pct >= minLabelPct ? `${pct.toFixed(1)}%` : '';
        return { d, x, y, anchor, text, pct };
      });

      const resolveCollisions = (labels: LabelDatum[]) => {
        labels.sort((a, b) => a.y - b.y);
        let maxIterations = 20;
        while (maxIterations > 0) {
          let hasCollision = false;
          for (let i = 1; i < labels.length; i++) {
            const prev = labels[i - 1];
            const curr = labels[i];
            const diff = curr.y - prev.y;
            if (diff < minGap) {
              const overlap = minGap - diff;
              prev.y -= overlap / 2;
              curr.y += overlap / 2;
              hasCollision = true;
            }
          }
          if (!hasCollision) break;
          maxIterations--;
        }
      };

      const rightLabels = allLabels.filter(l => l.anchor === 'start');
      const leftLabels = allLabels.filter(l => l.anchor === 'end');
      resolveCollisions(rightLabels);
      resolveCollisions(leftLabels);

      const labelGroup = svg.append('g')
        .attr('class', 'pie-labels')
        .style('pointer-events', 'none');
      [...rightLabels, ...leftLabels].forEach(label => {
        if (!label.text) return;
        labelGroup.append('text')
          .attr('class', `pie-label label-cat-${label.d.data.categoryIndex}`)
          .attr('transform', `translate(${label.x}, ${label.y})`)
          .attr('text-anchor', label.anchor)
          .style('font-size', `${labelFontSize}px`)
          .style('font-weight', '600')
          .style('fill', colorScale(label.d.data.categoryIndex.toString()))
          .text(label.text);
      });
    }


    if (this.showLegend && legendContainer) {
      const legend = legendContainer;
      legend
        .selectAll('div')
        .data(this.data)
        .enter()
        .append('div')
        .attr(
          'class',
          this.showLegendValues
            ? 'flex items-center justify-between w-full gap-8 text-sm whitespace-nowrap mb-3'
            : 'flex items-center gap-2 text-sm whitespace-nowrap'
        )
        .html((d, i) => {
          const legendColor = d['color'] || colors.sliceColors[i % colors.sliceColors.length];
          const labelHtml = `
           <div class="flex items-center gap-2">
              <span style="width:10px;height:10px;background:${legendColor};border-radius:3px;display:inline-block;"></span>
              <span style="color:${this.legendTextColored ? legendColor : colors.subtitleColor};font-weight:500;font-size:13px;">${d.label}</span>
           </div>
          `;
          if (this.showLegendValues) {
            return labelHtml + `<div style="color:${colors.text};font-weight:700;font-size:14px;">${(d.value || 0).toLocaleString()}</div>`;
          }
          return labelHtml;
        });
    }

    // Semi-circle or Donut: draw center label (value + subtitle)
    if (isSemi || this.isDonut) {
      const centerVal = this.centerValue !== null ? this.centerValue : (isSemi ? (this.data[0]?.value ?? '') : '');
      const centerLbl = this.centerLabel || '';
      const textColor = this.currentTheme === 'dark' ? '#ffffff' : '#111827';
      const subColor = this.currentTheme === 'dark' ? '#9ca3af' : '#6b7280';
      const isDonutLayout = this.isDonut && !isSemi;

      // Group for text. For donut, apply it to root svgEl to avoid 3D squash, else use transformed svg group
      const textGroup = isDonutLayout
        ? svgEl.append('g').attr('transform', `translate(${pieCenterX}, ${pieCenterY})`)
        : svg;
      textGroup.style('pointer-events', 'none');

      // Sub label
      if (centerLbl) {
        textGroup.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', isDonutLayout ? '-0.6em' : '1.4em')
          .style('font-size', isDonutLayout ? '16px' : `${Math.max(radius * 0.13, 11)}px`)
          .style('font-weight', '500')
          .style('fill', subColor)
          .style('font-family', 'Inter, sans-serif')
          .text(centerLbl);
      }

      // Main value
      if (centerVal !== '') {
        textGroup.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', isDonutLayout ? '0.6em' : '-0.2em')
          .style('font-size', isDonutLayout ? '34px' : `${Math.max(radius * 0.28, 18)}px`)
          .style('font-weight', '700')
          .style('fill', isDonutLayout ? textColor : (this.data[0]?.color || '#ec4899'))
          .style('font-family', 'Inter, sans-serif')
          .text(typeof centerVal === 'number' ? centerVal.toLocaleString() : centerVal);
      }
    }

    if (this.tightViewBox()) {
      this.applyTightViewBox(svgEl);
    }
  }

  private applyTightViewBox(svgEl: d3.Selection<SVGSVGElement, unknown, null, undefined>): void {
    const node = svgEl.node();
    if (!node) return;

    try {
      const bbox = node.getBBox();
      if (bbox.width <= 0 || bbox.height <= 0) return;
      const pad = 8;
      svgEl.attr(
        'viewBox',
        `${bbox.x - pad} ${bbox.y - pad} ${bbox.width + pad * 2} ${bbox.height + pad * 2}`
      );
    } catch {
      // getBBox can fail before the browser lays out SVG text
    }
  }

  /** Estimated horizontal space for the widest polyline label + value stack. */
  private getPolylineLabelTextPad(): number {
    const labels = this.data.map((d) => String(d.label ?? ''));
    const values = this.data.map((d) => {
      const raw = d.displayValue ?? d.value ?? 0;
      return typeof raw === 'number' ? raw.toLocaleString() : String(raw);
    });
    const maxChars = Math.max(0, ...labels.map((l) => l.length), ...values.map((v) => v.length));
    return Math.min(72, Math.max(48, maxChars * 6.5));
  }

  ngOnDestroy(): void {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}
